// Where a new account came from: what the site's trial link carries, in the names the site's contact form already sends
// (`attribution()` in apps/web/src/client.ts, read by `attributionOf` in packages/leads/src/forms.ts), with the same length limits.
import { z } from "zod";
import { cleanPageUrl } from "./page-url";
import { SIGNUP_SOURCE_LIMITS, SIGNUP_SOURCE_NAMES, SIGNUP_SOURCE_URLS, type SignupSourceName } from "./signup-source-names";

export { SIGNUP_SOURCE_IDENTIFIERS, SIGNUP_SOURCE_LIMITS, SIGNUP_SOURCE_NAMES, SIGNUP_SOURCE_URLS, type SignupSourceName } from "./signup-source-names";

export const SignupSource = z.strictObject(
  Object.fromEntries(SIGNUP_SOURCE_NAMES.map((name) => [name, z.string().trim().min(1).max(SIGNUP_SOURCE_LIMITS[name]).optional()])) as {
    [K in SignupSourceName]: z.ZodOptional<z.ZodString>;
  },
);
export type SignupSource = Partial<Record<SignupSourceName, string>>;

const URL_NAMES: ReadonlySet<SignupSourceName> = new Set(SIGNUP_SOURCE_URLS);

/**
 * The source out of an address's query or any bag of values: only the known names, trimmed, each within its limit. A value over its
 * limit is dropped rather than cut, so a clipped click id never reaches an ad network. `landing_url` and `referrer` are kept as
 * `cleanPageUrl` gives them, and dropped when they are not http(s): a caller can skip the site, and a sign-in never fails over where
 * someone came from. Null when nothing is left.
 */
export function pickSignupSource(values: URLSearchParams | Readonly<Record<string, unknown>>): SignupSource | null {
  const read = (name: string): unknown => (values instanceof URLSearchParams ? values.get(name) : values[name]);
  const out: SignupSource = {};
  for (const name of SIGNUP_SOURCE_NAMES) {
    const raw = read(name);
    if (typeof raw !== "string") continue;
    const value = URL_NAMES.has(name) ? (cleanPageUrl(raw.trim()) ?? "") : raw.trim();
    if (value.length > 0 && value.length <= SIGNUP_SOURCE_LIMITS[name]) out[name] = value;
  }
  return Object.keys(out).length > 0 ? out : null;
}
